import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box, Stack, CircularProgress, Typography } from "@mui/material";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import {
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  useGridApiContext,
  useGridSelector,
  gridDetailPanelExpandedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { useData } from "./data";
import { ByzMuiDatePicker } from "src/components/core-components/mui/inputs/ByzMuiDatePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";

const CustomToolbar = React.memo(() => (
  <GridToolbarContainer sx={{ color: "#00B8D9" }}>
    <GridToolbar />
    <GridToolbarQuickFilter placeholder="Ara" sx={{ ml: "auto" }} />
  </GridToolbarContainer>
));

const DetailPanelToggleCell = ({ id }: { id: any }) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);
  const isExpanded = expandedRowIds.includes(id);

  const handleToggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    apiRef.current.toggleDetailPanel(id);
  }, [apiRef, id]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        justifyContent: "center",
        cursor: "pointer",
      }}
      onClick={handleToggle}
    >
      <KeyboardArrowRightIcon
        sx={{
          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </Box>
  );
};

// --- Helpers ---
function setCheckedRecursive(row: any, checked: boolean, checkedNcpdIds: any[] = [], addedNcpdIds: any[] = []): void {
  if (row.ncpd_id && checkedNcpdIds.includes(row.ncpd_id) && !row.checked) {
    row.checked = false;
  } else if (row.ncpd_id && addedNcpdIds.includes(row.ncpd_id)) {
    row.checked = false;
  } else {
    row.checked = checked;
    if (row.ncpd_id && checked) {
      checkedNcpdIds.push(row.ncpd_id);
    }
  }
  if (row.childData && Array.isArray(row.childData)) {
    row.childData.forEach((childRow: any) => {
      setCheckedRecursive(childRow, checked, checkedNcpdIds, addedNcpdIds);
    });
  }
}

function updateCheckStateRecursive(rows: any[], rowId: number, checked: boolean, checkedNcpdIds: any[], addedNcpdIds: any[] = []): any[] {
  return rows.map((row: any): any => {
    if (row.id === rowId) {
      const newRow = { ...row };
      setCheckedRecursive(newRow, checked, checkedNcpdIds, addedNcpdIds);
      return newRow;
    } else if (row.childData) {
      const newChildData: any[] = updateCheckStateRecursive(row.childData, rowId, checked, checkedNcpdIds, addedNcpdIds);
      const newRow: any = { ...row, childData: newChildData };
      if (newChildData.length > 0) {
        const allChildrenChecked = newChildData.every((child: any) => child.checked);
        const anyChildChecked = newChildData.some((child: any) => child.checked);
        newRow.checked = allChildrenChecked ? true : (anyChildChecked ? newRow.checked : false);
      }
      return newRow;
    }
    return row;
  });
}

function collectCheckedRows(rows: any[], guide?: any, plan?: any): any[] {
  let result: any[] = [];
  rows.forEach((row) => {
    if (row.childData && Array.isArray(row.childData) && row.childData.length > 0) {
      const nextGuide = guide || row;
      const nextPlan = guide ? (plan || row) : undefined;
      result = result.concat(collectCheckedRows(row.childData, nextGuide, nextPlan));
    } else if (row.checked && row.ncpd_id) {
      result.push({
        ...row,
        nursingInitiative: row.name || row.title,
        ncpId: plan ? plan.ncp_id : undefined,
        guideName: guide?.title || guide?.name,
        planName: plan?.title || plan?.name,
      });
    }
  });
  return result;
}

function clearAllSelections(rows: any[]): any[] {
  return rows.map(row => ({
    ...row,
    checked: false,
    childData: row.childData ? clearAllSelections(row.childData) : undefined
  }));
}

interface PageOneProps {
  ptId?: number;
  onAddSelectedRows?: (rows: any[]) => void;
  onClose?: () => void;
}

function PageOne({ ptId, onAddSelectedRows, onClose }: PageOneProps) {
  const { t, outerGridRows, secondGridColumns, thirdGridColumns, isLoading, isFetching } = useData(ptId);
  const [outerDataState, setOuterDataState] = useState<any[]>([]);
  const [addedNcpdIds, setAddedNcpdIds] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Sync state with query data
  useEffect(() => {
    setOuterDataState(outerGridRows);
  }, [outerGridRows]);

  // Derived selections
  const { selectedRowsWithData, checkedNcpdIds } = useMemo(() => {
    const checked = collectCheckedRows(outerDataState);
    return {
      selectedRowsWithData: checked,
      checkedNcpdIds: checked.map(row => row.ncpd_id).filter(Boolean)
    };
  }, [outerDataState]);

  const handleStartDateChange = useCallback((date: Date | null) => {
    setStartDate(date);
    if (date && endDate && date > endDate) {
      setEndDate(date);
    }
  }, [endDate]);

  const onAddClick = useCallback(() => {
    const newRows = selectedRowsWithData.map(row => ({
      ...row,
      startDate: startDate ? new Date(startDate).toISOString().slice(0, 10) : "",
      endDate: endDate ? new Date(endDate).toISOString().slice(0, 10) : "",
    }));

    onAddSelectedRows?.(newRows);

    setAddedNcpdIds(prev => [
      ...prev,
      ...newRows.map(row => row.ncpd_id).filter(Boolean)
    ]);
    setOuterDataState(prev => clearAllSelections([...prev]));
  }, [selectedRowsWithData, startDate, endDate, onAddSelectedRows]);

  const handleCheckChange = useCallback((rowId: number, isChecked: boolean) => {
    setOuterDataState(prev => updateCheckStateRecursive(
      [...prev],
      rowId,
      isChecked,
      [...checkedNcpdIds],
      addedNcpdIds
    ));
  }, [checkedNcpdIds, addedNcpdIds]);

  // Column Definitions
  const thirdColumns = useMemo(() => thirdGridColumns.map((col) => {
    if (col.field === "check") {
      return {
        ...col,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as any;
          return (
            <ByzMuiCheckBox
              checked={row.checked ?? false}
              disabled={(!!row.ncpd_id && checkedNcpdIds.includes(row.ncpd_id) && !row.checked) || (!!row.ncpd_id && addedNcpdIds.includes(row.ncpd_id))}
              onChange={(e) => handleCheckChange(row.id, e.target.checked)}
            />
          );
        },
      };
    }
    return col;
  }), [thirdGridColumns, checkedNcpdIds, addedNcpdIds, handleCheckChange]);

  const secondColumns = useMemo(() => secondGridColumns.map((col) => {
    if (col.field === "check") {
      return {
        ...col,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as any;
          return (
            <ByzMuiCheckBox
              checked={row.checked ?? false}
              disabled={(!!row.ncpd_id && checkedNcpdIds.includes(row.ncpd_id) && !row.checked) || (!!row.ncpd_id && addedNcpdIds.includes(row.ncpd_id))}
              onChange={(e) => handleCheckChange(row.id, e.target.checked)}
            />
          );
        },
      };
    }
    return col;
  }), [secondGridColumns, checkedNcpdIds, addedNcpdIds, handleCheckChange]);

  const outerColumns = useMemo((): GridColDef[] => [
    {
      field: "check",
      headerName: "",
      width: 62,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as any;
        return (
          <ByzMuiCheckBox
            checked={row.checked ?? false}
            disabled={(!!row.ncpd_id && checkedNcpdIds.includes(row.ncpd_id) && !row.checked) || (!!row.ncpd_id && addedNcpdIds.includes(row.ncpd_id))}
            onChange={(e) => handleCheckChange(row.id, e.target.checked)}
          />
        );
      },
    },
    {
      field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
      headerName: "",
      width: 50,
      renderCell: (params) => {
        const row = params.row as any;
        return (row.childData?.length > 0) ? <DetailPanelToggleCell id={params.id} /> : null;
      },
    },
    { field: "title", headerName: "", flex: 1 },
  ], [checkedNcpdIds, addedNcpdIds, handleCheckChange]);

  // Nested Grid Renderers
  const renderThirdGrid = useCallback((params: GridRenderCellParams) => {
    const rows = (params.row as any).childData || [];
    return (
      <Box sx={{ py: 1, pr: 1, pl: 8, display: "flex", flexDirection: "column" }}>
        <ByzMuiDataGrid
          rows={rows}
          columns={thirdColumns}
          hideFooter
          pagination={false}
          autoHeight
          columnHeaderHeight={0}
          sx={{
            "& .MuiDataGrid-columnHeaders": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { marginTop: "0 !important" },
          }}
        />
      </Box>
    );
  }, [thirdColumns]);

  const renderSecondGrid = useCallback((params: GridRenderCellParams) => {
    const rows = (params.row as any).childData || [];
    return (
      <Box sx={{ py: 1, pr: 1, pl: 8, display: "flex", flexDirection: "column" }}>
        <ByzMuiDataGrid
          slots={{
            detailPanelExpandIcon: KeyboardArrowRightIcon,
            detailPanelCollapseIcon: KeyboardArrowDownIcon,
          }}
          rows={rows}
          columns={secondColumns}
          getDetailPanelContent={renderThirdGrid}
          getDetailPanelHeight={() => 'auto'}
          autoHeight
          hideFooter
          pagination={false}
          columnHeaderHeight={0}
          sx={{
            "& .MuiDataGrid-columnHeaders": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { marginTop: "0 !important" },
          }}
        />
      </Box>
    );
  }, [secondColumns, renderThirdGrid]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={3} justifyContent="space-between" alignItems="center">
        <ByzMuiTypography isTitle={true} sx={{ ml: 0 }}>
          {t("patient-care-guide.care-planing")}
        </ByzMuiTypography>
        <ByzMuiCloseButton onClick={onClose || (() => { })} />
      </Stack>

      <Stack direction="row" spacing={3} sx={{ pb: 2, pt: 2 }}>
        <ByzMuiDatePicker
          label={t("patient-care.first-day")}
          value={startDate}
          onChange={handleStartDateChange}
          disablePast
          minDate={new Date()}
        />
        <ByzMuiDatePicker
          label={t("patient-care.last-day")}
          value={endDate}
          onChange={setEndDate}
          minDate={startDate ?? undefined}
        />
        <ByzMuiAddButton variant="contained" color="primary" onClick={onAddClick} />
      </Stack>

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {isLoading || isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
          </Box>
        ) : (
          <ByzMuiDataGrid
            slots={{
              detailPanelExpandIcon: KeyboardArrowRightIcon,
              detailPanelCollapseIcon: KeyboardArrowDownIcon,
            }}
            customToolbar={CustomToolbar}
            rows={outerDataState}
            columns={outerColumns}
            hideFooter
            pagination={false}
            columnHeaderHeight={0}
            sx={{
              "& .MuiDataGrid-columnHeaders": { display: "none" },
              "& .MuiDataGrid-virtualScroller": { marginTop: "0 !important" },
            }}
            getRowClassName={(params) => addedNcpdIds.includes(params.row.ncpd_id) ? 'disabled-row' : ''}
            getDetailPanelContent={renderSecondGrid}
            getDetailPanelHeight={() => 'auto'}
          />
        )}
      </Box>
    </Box>
  );
}

export default PageOne;
